import { AddressSearchField, type AddressSuggestion, type Item } from "@wayflyer/flyui";
import { IconMapPin16Line } from "@wayflyer/flyui-icons/16/line";
import { useCallback, useMemo, useRef } from "react";
import { useWatch } from "react-hook-form";

import { AddressManualEntryModal } from "./address-manual-entry-modal";
import { formatAddressValueLabel } from "./format-address-value-label";
import { useJurisdictionLabels } from "./jurisdiction-labels";
import type { EditableAddressSearchFieldProps } from "./types";
import { useEditableAddressSearch } from "./use-editable-address-search";

export interface RhfAddressSearchFieldProps extends EditableAddressSearchFieldProps {
  /** Label for the "Enter manually" action shown in the menu. */
  manualEntryLabel?: string;
  /** Custom formatter for the selected value shown in the trigger (e.g. banking cards). */
  formatValueLabel?: (item: Item) => string;
}

/**
 * Wires the fefo `AddressSearchField` and the local `AddressManualEntryModal` to
 * react-hook-form and the Google Places hook. This is the single stateful address
 * search used across the app; the data fetching and API key stay here, not in the
 * design system.
 */
export const RhfAddressSearchField = ({
  form,
  handleAddressSelect,
  helperText,
  label = "Address finder",
  placeholder = "Search for address",
  fieldName = "address_search",
  clearable = false,
  countries,
  states,
  parentFormFieldName,
  onBlur,
  onAddressChange,
  fieldMapping,
  lockFields,
  notification,
  disabled,
  errorMessage,
  onModalSave,
  manualEntryLabel = "Enter manually",
  formatValueLabel = formatAddressValueLabel,
}: RhfAddressSearchFieldProps) => {
  const isOpeningManualEntryRef = useRef(false);
  const { getStateLabel, getPostalCodeLabel } = useJurisdictionLabels();

  const handleManualEntryClick = () => {
    isOpeningManualEntryRef.current = true;
    handleManualEntry(true);
  };

  const {
    addressAutocomplete,
    transformedOptions,
    handleAddressChange: internalHandleAddressChange,
    isManualEntryModalOpen,
    isProcessingSelectionRef,
    manualEntryAddress,
    handleManualEntry,
    handleModalSave,
    handleModalCancel,
    globalActionConfig,
  } = useEditableAddressSearch({
    form,
    handleAddressSelect,
    fieldName,
    countries,
    states,
    onManualEntry: handleManualEntryClick,
    fieldMapping,
    parentFormFieldName,
  });

  const formFieldValue = useWatch({ control: form.control, name: fieldName });

  // eslint-disable-next-line react-hooks/preserve-manual-memoization -- mirrors EditableAddressSearchField
  const isOptionId = useMemo(() => {
    if (!formFieldValue || typeof formFieldValue !== "string") {
      return false;
    }
    return transformedOptions.some((opt) => opt.id === formFieldValue);
  }, [transformedOptions, formFieldValue]);

  const results = useMemo<AddressSuggestion[]>(() => {
    if (!formFieldValue || typeof formFieldValue !== "string" || isOptionId) {
      return transformedOptions;
    }

    // Keep a manually-entered / free-text value renderable in the trigger.
    return [
      {
        id: formFieldValue,
        text: formFieldValue,
        subText:
          addressAutocomplete.selectedAddress?.city && addressAutocomplete.selectedAddress?.state
            ? `${addressAutocomplete.selectedAddress.city}, ${addressAutocomplete.selectedAddress.state}`
            : "",
        icon: <IconMapPin16Line />,
      },
      ...transformedOptions,
    ];
  }, [transformedOptions, formFieldValue, isOptionId, addressAutocomplete.selectedAddress]);

  const handleSelect = useCallback(
    (optionId: string | null) => {
      if (optionId === null) {
        const currentSearchValue = form.getValues(fieldName);
        if (currentSearchValue) {
          internalHandleAddressChange(null);
          onAddressChange?.(null);
        } else {
          form.setValue(fieldName, "", { shouldDirty: true });
          addressAutocomplete.clear();
          if (fieldMapping) {
            form.setValue(fieldMapping.address_line_1, "", { shouldDirty: true });
            form.setValue(fieldMapping.address_line_2, "", { shouldDirty: true });
            form.setValue(fieldMapping.city, "", { shouldDirty: true });
            form.setValue(fieldMapping.state, "", { shouldDirty: true });
            form.setValue(fieldMapping.zip_code, "", { shouldDirty: true });
            form.setValue(fieldMapping.country_code, "", { shouldDirty: true });
          }
          if (parentFormFieldName) {
            form.resetField(parentFormFieldName);
          }
        }
        return;
      }

      // Bind to RHF the way RhfAutocompleteField did: set the field, clear stale errors,
      // then run the address selection (which fetches details and fills mapped fields).
      form.setValue(fieldName, optionId, { shouldDirty: true });
      if (form.getFieldState(fieldName, form.formState).error) {
        void form.trigger(fieldName);
      }
      internalHandleAddressChange(optionId);
      onAddressChange?.(optionId);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mirrors EditableAddressSearchField
    [internalHandleAddressChange, onAddressChange, fieldName, fieldMapping, parentFormFieldName]
  );

  const handleBlur = useCallback(
    (event: unknown) => {
      // Don't run blur logic while a selection is in flight or the modal is open.
      if (isOpeningManualEntryRef.current || isManualEntryModalOpen || isProcessingSelectionRef.current) {
        isOpeningManualEntryRef.current = false;
        isProcessingSelectionRef.current = false;
        return;
      }

      const searchTerm = addressAutocomplete.searchTerm;
      if (!isOptionId && searchTerm) {
        form.setValue(fieldName, searchTerm, { shouldDirty: true });
        form.clearErrors(fieldName);
      }

      form.register(fieldName).onBlur(event as Parameters<ReturnType<typeof form.register>["onBlur"]>[0]);
      void form.trigger(fieldName);
      onBlur?.();
    },
    [
      isManualEntryModalOpen,
      isProcessingSelectionRef,
      addressAutocomplete.searchTerm,
      isOptionId,
      form,
      fieldName,
      onBlur,
    ]
  );

  const rhfError = form.getFieldState(fieldName, form.formState).error?.message;

  return (
    <>
      <AddressSearchField
        name={fieldName}
        results={results}
        value={typeof formFieldValue === "string" ? formFieldValue : null}
        loading={addressAutocomplete.fetching}
        label={label}
        placeholder={placeholder}
        helperText={helperText}
        error={errorMessage ?? rhfError}
        disabled={disabled}
        clearable={clearable}
        onSearchTermChange={addressAutocomplete.onSearchTermChange}
        onSelect={handleSelect}
        onManualEntry={disabled ? undefined : globalActionConfig.onAction}
        manualEntryLabel={manualEntryLabel}
        formatValueLabel={formatValueLabel}
        onBlur={handleBlur}
      />

      <AddressManualEntryModal
        isOpen={isManualEntryModalOpen}
        initialAddress={manualEntryAddress}
        countries={countries}
        lockFields={lockFields}
        notification={notification}
        getStateLabel={getStateLabel}
        getPostalCodeLabel={getPostalCodeLabel}
        onSave={(values) => {
          handleModalSave(values);
          onModalSave?.();
        }}
        onCancel={handleModalCancel}
      />
    </>
  );
};
